import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle } from 'lucide-react';
import { portalApi } from '../lib/api';

interface ClientRequest {
  id: string;
  title: string;
  description: string;
  dueDate: string | null;
  status: string;
  createdAt: string;
}

export function RequestsPage() {
  const { t } = useTranslation();
  const [requests, setRequests] = useState<ClientRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    portalApi
      .getRequests()
      .then((res: { data: ClientRequest[] }) => setRequests(res.data))
      .catch(() => setError(t('portal.uploadError')))
      .finally(() => setLoading(false));
  }, [t]);

  const statusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'border-l-warning';
      case 'SEEN': return 'border-l-primary';
      case 'COMPLETED': return 'border-l-success';
      case 'EXPIRED': return 'border-l-muted-foreground';
      default: return 'border-l-muted-foreground';
    }
  };

  return (
    <div className="p-4">
      <h2 className="text-lg font-bold mb-4">{t('portal.requests')}</h2>

      {loading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="animate-pulse bg-muted rounded-lg h-20" />
          ))}
        </div>
      ) : error ? (
        <p className="text-sm text-destructive text-center py-8">{error}</p>
      ) : !requests.length ? (
        <div className="text-center py-8">
          <div className="w-14 h-14 rounded-2xl bg-muted/60 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-7 w-7 text-muted-foreground/40" />
          </div>
          <p className="text-sm text-muted-foreground">{t('portal.noRequests')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => (
            <div
              key={req.id}
              className={`bg-card rounded-2xl border border-border border-l-4 p-3 shadow-lifted hover:shadow-lifted-lg transition-shadow ${statusColor(req.status)}`}
            >
              <p className="text-sm font-medium">{req.title}</p>
              {req.description && (
                <p className="text-xs text-muted-foreground mt-1">{req.description}</p>
              )}
              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                <span>
                  {new Date(req.createdAt).toLocaleDateString('el-GR', {
                    day: 'numeric',
                    month: 'short',
                  })}
                </span>
                {req.dueDate && (
                  <>
                    <span>&middot;</span>
                    <span className={new Date(req.dueDate) < new Date() ? 'text-destructive font-bold' : ''}>
                      {t('portal.dueBy', { date: new Date(req.dueDate).toLocaleDateString('el-GR', {
                        day: 'numeric',
                        month: 'short',
                      }) })}
                    </span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
